import React, { useState } from 'react';
import { Heart, Shield, Users, MessageCircle } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  isActive: boolean;
  onClick: () => void;
}

function FeatureCard({ icon: Icon, title, description, isActive, onClick }: FeatureCardProps) {
  return (
    <button 
      onClick={onClick}
      className={`w-full text-left group p-6 rounded-xl transition-all duration-300 transform hover:scale-105
        ${isActive 
          ? 'bg-primary-500 text-white shadow-lg scale-105' 
          : 'bg-white dark:bg-gray-800 hover:shadow-xl'}`}
    >
      <div className="relative">
        <div className={`absolute -inset-1 rounded-lg blur-lg transition-opacity duration-300
          ${isActive ? 'opacity-50 bg-primary-300' : 'opacity-0 group-hover:opacity-30 bg-primary-500'}`} 
        />
        <Icon className={`relative w-12 h-12 mb-4 transition-transform duration-300 group-hover:scale-110
          ${isActive ? 'text-white' : 'text-primary-500'}`} 
        />
      </div>
      <h3 className={`text-xl font-semibold mb-2 transition-colors
        ${isActive ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
        {title}
      </h3>
      <p className={`transition-colors
        ${isActive ? 'text-white/90' : 'text-gray-600 dark:text-gray-300'}`}>
        {description}
      </p>
    </button>
  );
}

export function InteractiveFeature() {
  const [activeFeature, setActiveFeature] = useState<number | null>(null);

  const features = [
    {
      icon: Users,
      title: 'Inclusive Community',
      description: 'Connect with understanding individuals who share similar experiences in a judgment-free environment.'
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'Your privacy is our priority. Advanced encryption and strict data protection measures keep your information secure.'
    },
    {
      icon: Heart,
      title: 'AI-Enhanced Matching',
      description: 'Our intelligent matching system helps you find meaningful connections based on compatibility and shared experiences.'
    },
    {
      icon: MessageCircle,
      title: 'Expert Q&A',
      description: 'Get answers from verified medical professionals and AI-powered assistance while maintaining your privacy.'
    }
  ];

  return (
    <section id="features" className="py-20 px-6 relative overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50/80 to-white dark:from-gray-900/80 dark:to-gray-800/80 backdrop-blur-sm" />
      
      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Why Choose CloseCircle
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            We're building a platform that prioritizes your privacy, safety, and comfort 
            while helping you find meaningful connections.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              {...feature}
              isActive={activeFeature === index}
              onClick={() => setActiveFeature(activeFeature === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}